"use server";

import {
  TApplyJobPayload,
  TCreateJobPayload,
  TCreateJobTaskPayload,
  TGenericResponse,
  TJob,
  TJobApplication,
  TJobCandidate,
  TJobCandidateDetails,
  TJobTask,
  TMyJobGroup,
  TMyTask,
  TResponseServerAction,
  TSubmitTaskPayload,
  TUpdateJobPayload,
} from "./types";
import { api, ErrorResponseData, type AxiosError } from "@/lib/axios";
import { getCookieByName } from "@/lib/cookies";
import { revalidatePath } from "next/cache";

/** Parse ky/ASP.NET-style bodies so we do not show a generic message on 409 etc. */
function extractApplyJobError(error: unknown): {
  httpStatus?: number;
  message: string;
  isConflict: boolean;
} {
  const ax = error as AxiosError<ErrorResponseData> & {
    response?: { status?: number; data?: unknown };
  };
  const httpStatus = ax.response?.status;
  const raw = ax.response?.data;
  let message = "";
  if (raw && typeof raw === "object") {
    const d = raw as unknown as Record<string, unknown>;
    message =
      (typeof d.message === "string" && d.message.trim()) ||
      (typeof d.Message === "string" && d.Message.trim()) ||
      (typeof (raw as ErrorResponseData).message === "string" &&
        (raw as ErrorResponseData).message.trim()) ||
      (typeof d.title === "string" && d.title.trim()) ||
      (typeof d.detail === "string" && d.detail.trim()) ||
      "";
  }
  if (!message && error instanceof Error && error.message) {
    message = error.message.trim();
  }
  if (!message) {
    message = "Job application failed";
  }
  return {
    httpStatus,
    message,
    isConflict: httpStatus === 409,
  };
}

/**
 * POST /api/Jobs/{companyProfileId}
 * Create a new job posting for a company
 */
export const createJob = async (
  companyProfileId: string,
  payload: TCreateJobPayload
): Promise<TResponseServerAction<TJob>> => {
  try {
    const response = await api.post<TGenericResponse<TJob>>(`/Jobs`, payload, {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    });

    revalidatePath("/");
    revalidatePath(`/profiles/${companyProfileId}`);

    return {
      status: "success",
      message: "Job created successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Job creation failed"
          : "Job creation failed",
    };
  }
};

/**
 * POST /api/Jobs/submit-task
 * Submit a task related to a job
 */
export const submitTask = async (
  payload: TSubmitTaskPayload
): Promise<TResponseServerAction<TJobTask>> => {
  console.log("[Server:submitTask] Starting submitTask");
  console.log("[Server:submitTask] Payload:", {
    JobId: payload.JobId,
    TaskId: payload.TaskId,
    SubmittedTo: payload.SubmittedTo,
    CountryShortName: payload.CountryShortName,
    VideoFileName: payload.VideoFile?.name,
    VideoFileSize: payload.VideoFile?.size,
    VideoFileType: payload.VideoFile?.type,
    ThumbnailFileName: payload.ThumbnailFile?.name,
    ThumbnailFileSize: payload.ThumbnailFile?.size,
    ThumbnailFileType: payload.ThumbnailFile?.type,
  });

  try {
    const formData = new FormData();
    formData.append("JobId", payload.JobId);
    formData.append("TaskId", payload.TaskId);
    formData.append("VideoFile", payload.VideoFile);
    formData.append("ThumbnailFile", payload.ThumbnailFile);
    formData.append("SubmittedTo", payload.SubmittedTo);
    formData.append("CountryShortName", payload.CountryShortName);

    console.log("[Server:submitTask] FormData created, making API request...");
    const startTime = Date.now();

    const response = await api.post<TGenericResponse<TJobTask>>(
      `/Jobs/submit-task`,
      formData,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      }
    );

    console.log("[Server:submitTask] API request completed in:", Date.now() - startTime, "ms");
    console.log("[Server:submitTask] Response status:", response.status);
    console.log("[Server:submitTask] Response data:", response.data);

    // revalidatePath("/jobs");

    return {
      status: "success",
      message: "Task submitted successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    console.error("[Server:submitTask] Error occurred:", error);
    const axiosError = error as AxiosError<ErrorResponseData>;

    if (axiosError.response) {
      console.error("[Server:submitTask] Error response status:", axiosError.response.status);
      console.error("[Server:submitTask] Error response data:", axiosError.response.data);
    } else if (axiosError.request) {
      console.error("[Server:submitTask] No response received. Request:", axiosError.request);
    } else {
      console.error("[Server:submitTask] Error message:", axiosError.message);
    }

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Task submission failed"
          : "Task submission failed",
    };
  }
};

/**
 * POST /api/Jobs/{jobId}/apply
 * Apply to a job posting
 */
export const applyToJob = async (
  jobId: string,
  payload: TApplyJobPayload
): Promise<TResponseServerAction<TJobApplication>> => {
  console.log({ payload });
  try {
    const response = await api.post<TGenericResponse<TJobApplication>>(
      `/Jobs/${jobId}/apply`,
      payload,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      }
    );

    return {
      status: "success",
      message: "Job application submitted successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const { message, isConflict } = extractApplyJobError(error);
    return {
      status: "error",
      message,
      ...(isConflict ? { errorType: "conflict" as const } : {}),
    } as TResponseServerAction<TJobApplication>;
  }
};

/**
 * GET /api/Jobs/{jobId}
 * Get job details by ID
 */
export const getJobById = async (
  jobId: string
): Promise<TResponseServerAction<TJob>> => {
  try {
    const response = await api.get<TGenericResponse<TJob>>(`/Jobs/${jobId}`, {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    });

    return {
      status: "success",
      message: "Job details fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Job details fetch failed"
          : "Job details fetch failed",
    };
  }
};

export const getJobByCompanyId = async (
  companyId: string
): Promise<TResponseServerAction<{ companyName: string; jobs: TMyTask[] }>> => {
  try {
    const response = await api.get<
      TGenericResponse<{ companyName: string; jobs: TMyTask[] }>
    >(`/Jobs/${companyId}/jobs`, {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    });

    return {
      status: "success",
      message: "Job details fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Job details fetch failed"
          : "Job details fetch failed",
    };
  }
};

/**
 * GET /api/profiles/{profileId}/jobs
 * Get jobs for a specific profile
 */
export const getJobsByProfileId = async (
  profileId: string
): Promise<TResponseServerAction<{ companyName: string; jobs: TJob[] }>> => {
  try {
    const response = await api.get<
      TGenericResponse<{ companyName: string; jobs: TJob[] }>
    >(`/profiles/${profileId}/jobs`, {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    });

    return {
      status: "success",
      message: "Jobs fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Jobs fetch failed"
          : "Jobs fetch failed",
    };
  }
};

/**
 * GET /api/Jobs/{jobId}/mytasks
 * Get tasks for a specific job
 */
export const getMyTasks = async (
  jobId: string
): Promise<TResponseServerAction<TMyTask[]>> => {
  try {
    const response = await api.get<TGenericResponse<TMyTask[]>>(
      `/Jobs/${jobId}/mytasks`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      }
    );

    return {
      status: "success",
      message: "Tasks fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Tasks fetch failed"
          : "Tasks fetch failed",
    };
  }
};

/**
 * GET /api/Jobs/{jobId}/candidates
 * Get candidates for a specific job
 */
export const getJobCandidates = async (
  jobId: string
): Promise<
  TResponseServerAction<{
    id: string;
    tags: string[];
    videoLink: string;
    thumbnailLink: string;
    candidates: TJobCandidate[];
  }>
> => {
  try {
    const response = await api.get<
      TGenericResponse<{
        id: string;
        tags: string[];
        videoLink: string;
        thumbnailLink: string;
        candidates: TJobCandidate[];
      }>
    >(`/Jobs/${jobId}/candidates`, {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    });

    return {
      status: "success",
      message: "Candidates fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Candidates fetch failed"
          : "Candidates fetch failed",
    };
  }
};

/**
 * GET /api/Jobs/{jobId}/candidates/{personalProfileId}
 * Get candidate details for a specific job application
 */
export const getJobCandidateDetails = async (
  jobId: string,
  personalProfileId: string
): Promise<TResponseServerAction<TJobCandidateDetails>> => {
  try {
    const response = await api.get<TGenericResponse<TJobCandidateDetails>>(
      `/Jobs/${jobId}/candidates/${personalProfileId}`,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      }
    );

    return {
      status: "success",
      message: "Candidate details fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message ||
            "Candidate details fetch failed"
          : "Candidate details fetch failed",
    };
  }
};

/**
 * PUT /api/Jobs/{jobId}
 * Update an existing job posting
 */
export const updateJob = async (
  jobId: string,
  payload: TUpdateJobPayload
): Promise<TResponseServerAction<TJob>> => {
  try {
    const response = await api.put<TGenericResponse<TJob>>(
      `/Jobs/${jobId}`,
      payload,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      }
    );

    revalidatePath("/");

    return {
      status: "success",
      message: "Job updated successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Job update failed"
          : "Job update failed",
    };
  }
};

/**
 * DELETE /api/Jobs/{jobId}
 * Delete a job posting
 */
export const deleteJob = async (
  jobId: string
): Promise<TResponseServerAction<null>> => {
  try {
    await api.delete(`/Jobs/${jobId}`, {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    });

    revalidatePath("/");

    return {
      status: "success",
      message: "Job deleted successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Job deletion failed"
          : "Job deletion failed",
    };
  }
};

/**
 * PUT /api/Jobs/task/{taskId}
 * Update an existing job task
 */
export const updateJobTask = async (
  taskId: string,
  payload: TCreateJobTaskPayload
): Promise<TResponseServerAction<TJobTask>> => {
  try {
    const response = await api.put<TGenericResponse<TJobTask>>(
      `/Jobs/task/${taskId}`,
      payload,
      {
        headers: {
          Authorization: await getCookieByName("x-api-key"),
        },
      }
    );

    revalidatePath("/");

    return {
      status: "success",
      message: "Job task updated successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Job task update failed"
          : "Job task update failed",
    };
  }
};

/**
 * GET /api/Jobs/my
 * Get my jobs
 */
export const getMyJobs = async (): Promise<TResponseServerAction<TMyJobGroup[]>> => {
  try {
    const response = await api.get<TGenericResponse<TMyJobGroup[]>>(`/Jobs/my`, {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    });

    return {
      status: "success",
      message: "My jobs fetched successfully",
      metaData: response.data.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "My jobs fetch failed"
          : "My jobs fetch failed",
    };
  }
};

/**
 * DELETE /api/Jobs/task/{taskId}
 * Delete a job task
 */
export const deleteJobTask = async (
  taskId: string
): Promise<TResponseServerAction<null>> => {
  try {
    await api.delete(`/Jobs/task/${taskId}`, {
      headers: {
        Authorization: await getCookieByName("x-api-key"),
      },
    });

    revalidatePath("/");

    return {
      status: "success",
      message: "Job task deleted successfully",
      metaData: null,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponseData>;

    return {
      status: "error",
      message:
        axiosError instanceof Error
          ? axiosError.response?.data?.message || "Job task deletion failed"
          : "Job task deletion failed",
    };
  }
};
